import TruncatedStringWithTooltip from '@components/TruncatedStringWithTooltip'
import { colors } from '@globals/themes/defaultTheme'
import { setSvgIconSize } from '@globals/themes/globalStyles'
import { MediaObjectTypeEnum, MediaObjectUrl } from '@kudos/http-client'
import AttachFileRounded from '@mui/icons-material/AttachFileRounded'
import Button from '@mui/material/Button'
import MuiLink from '@mui/material/Link'
import { isStandalonePWA } from '@shared/utils/isStandalonePWA'

const buttonStyle = {
  ...setSvgIconSize('1.5rem'),
  mt: { xs: 0, sm: 0 },
  minHeight: 40,
  pl: 2,
  '&:hover': {
    backgroundColor: colors.blueExtraLight,
  },
}

type DocumentAttachmentProps = {
  mediaObject: MediaObjectUrl
}

const isDocFile = (type: MediaObjectTypeEnum | undefined) => type === MediaObjectTypeEnum.DOC

const DocumentAttachment: React.FC<DocumentAttachmentProps> = ({ mediaObject }) => {
  /**
   * Only previewable types (PDFs) benefit from `target="_blank"` because the browser
   * can render them in a new tab. For DOCX/PPTX the response is `Content-Disposition:
   * attachment`, and pairing that with `target="_blank"` inside an iOS standalone PWA
   * navigates the standalone window to the file URL and breaks the page (SYN-6941).
   *
   * Standalone PWAs also can't actually open a new tab, so even for PDFs we let the
   * link navigate normally so iOS can show its native download/preview sheet.
   */
  const openInNewTab = isDocFile(mediaObject.type) && !isStandalonePWA()

  return (
    <Button
      component={MuiLink}
      href={mediaObject.url}
      target={openInNewTab ? '_blank' : undefined}
      sx={buttonStyle}
      rel={openInNewTab ? 'noopener noreferrer' : undefined}
      id={`btnDocumentAttachment-${mediaObject.originalFilename}`}
      variant="outlined"
      startIcon={<AttachFileRounded sx={{ color: `${colors.bluePrimary} !important` }} />}>
      <TruncatedStringWithTooltip name={mediaObject.originalFilename} ellipsisPosition="middle" />
    </Button>
  )
}

export default DocumentAttachment
